<template>
  <div class="handcopy-list">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>手抄报管理</span>
          <div>
            <el-button type="primary" @click="addDialogVisible = true">添加手抄报</el-button>
            <el-button type="success" @click="batchDialogVisible = true">批量导入</el-button>
            <el-input 
              v-model="searchKeyword" 
              placeholder="搜索标题" 
              style="width: 200px; margin-left: 10px"
              clearable
            />
            <el-button type="primary" @click="handleSearch" style="margin-left: 10px">搜索</el-button>
          </div>
        </div>
      </template>
      
      <el-table :data="handCopyList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="title" label="标题" />
        <el-table-column prop="category_name" label="分类" />
        <el-table-column prop="author" label="作者" />
        <el-table-column prop="status" label="状态">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="view_count" label="浏览量" />
        <el-table-column prop="created_at" label="创建时间" />
        <el-table-column label="操作" width="200">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">查看</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <el-pagination
        v-model:current-page="page"
        v-model:page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        style="margin-top: 20px"
        @current-change="handlePageChange"
      />
    </el-card>

    <!-- 添加手抄报对话框 -->
    <el-dialog v-model="addDialogVisible" title="添加手抄报" width="600px" destroy-on-close>
      <el-form :model="addForm" label-width="100px" :rules="addRules" ref="addFormRef">
        <el-form-item label="标题" prop="title">
          <el-input v-model="addForm.title" placeholder="请输入手抄报标题" />
        </el-form-item>
        <el-form-item label="分类" prop="category_id">
          <el-select v-model="addForm.category_id" placeholder="请选择分类" style="width: 100%">
            <el-option 
              v-for="item in categoryOptions" 
              :key="item.id" 
              :label="item.name" 
              :value="item.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="封面图片" prop="cover_url">
          <div style="display: flex; align-items: flex-start; gap: 10px;">
            <el-input 
              v-model="addForm.cover_url" 
              placeholder="图片URL，或上传图片自动生成" 
              style="flex: 1"
            />
            <el-upload
              action=""
              :show-file-list="false"
              :http-request="handleCoverUpload"
              accept="image/*"
              :before-upload="beforeImageUpload"
            >
              <el-button type="primary" size="small" :loading="coverUploading">
                <el-icon><Upload /></el-icon> 上传
              </el-button>
            </el-upload>
          </div>
          <el-image 
            v-if="addForm.cover_url" 
            :src="addForm.cover_url" 
            style="width: 120px; height: 80px; margin-top: 8px; border-radius: 4px;"
            fit="cover"
          />
        </el-form-item>
        <el-form-item label="内容描述" prop="content">
          <el-input 
            v-model="addForm.content" 
            type="textarea" 
            :rows="4" 
            placeholder="请输入手抄报内容描述"
          />
        </el-form-item>
        <el-form-item label="难度" prop="difficulty">
          <el-radio-group v-model="addForm.difficulty">
            <el-radio value="easy">简单</el-radio>
            <el-radio value="medium">中等</el-radio>
            <el-radio value="hard">困难</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="addForm.status">
            <el-radio :value="0">待审核</el-radio>
            <el-radio :value="1">已通过</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAddSubmit" :loading="addLoading">保存</el-button>
      </template>
    </el-dialog>

    <!-- 批量导入对话框 -->
    <el-dialog v-model="batchDialogVisible" title="批量导入手抄报" width="800px">
      <el-tabs v-model="batchActiveTab">
        <el-tab-pane label="JSON导入" name="json">
          <el-form label-width="100px">
            <el-form-item label="手抄报数据">
              <el-input 
                v-model="batchJson" 
                type="textarea" 
                :rows="10" 
                placeholder='[{&quot;title&quot;:&quot;手抄报1&quot;,&quot;category_id&quot;:1,&quot;difficulty&quot;:&quot;easy&quot;}]'
              />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="CSV导入" name="csv">
          <el-form label-width="100px">
            <el-form-item label="CSV数据">
              <el-input 
                v-model="batchCsv" 
                type="textarea" 
                :rows="10" 
                placeholder="title,category_id,cover_url,content,difficulty&#10;手抄报1,1,http://example.com/c1.jpg,内容,easy"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="info" size="small" @click="downloadTemplate">
                <el-icon><Download /></el-icon> 下载CSV模板
              </el-button>
            </el-form-item>
            <el-form-item>
              <el-alert 
                title="CSV格式说明" 
                type="info" 
                :closable="false"
                description="第一行为表头：title,category_id,cover_url,content,difficulty。每行一个手抄报，用逗号分隔。"
              />
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
      
      <!-- 导入结果 -->
      <div v-if="batchResult" class="batch-result">
        <el-divider />
        <el-alert 
          :title="`导入完成：成功 ${batchResult.success} 条，失败 ${batchResult.failed} 条`" 
          :type="batchResult.failed === 0 ? 'success' : 'warning'"
          :closable="false"
        />
        <el-table v-if="batchResult.errors && batchResult.errors.length > 0" :data="batchResult.errors" size="small" style="margin-top: 10px">
          <el-table-column prop="index" label="行号" width="80" />
          <el-table-column prop="error" label="错误信息" />
        </el-table>
      </div>
      
      <template #footer>
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleBatchImport" :loading="batchLoading">开始导入</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Upload } from '@element-plus/icons-vue'
import request from '../../utils/request'

export default {
  name: 'HandCopyList',
  setup() {
    const handCopyList = ref([])
    const loading = ref(false)
    const page = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const searchKeyword = ref('')
    
    // 添加手抄报相关
    const addDialogVisible = ref(false)
    const addForm = ref({
      title: '',
      category_id: '',
      cover_url: '',
      content: '',
      difficulty: 'easy',
      status: 1
    })
    const addLoading = ref(false)
    const coverUploading = ref(false)
    const categoryOptions = ref([])
    const addFormRef = ref(null)
    const addRules = {
      title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
      category_id: [{ required: true, message: '请选择分类', trigger: 'change' }],
      cover_url: [{ required: true, message: '请上传或输入封面图片', trigger: 'blur' }]
    }
    
    // 批量导入相关
    const batchDialogVisible = ref(false)
    const batchActiveTab = ref('json')
    const batchJson = ref('')
    const batchCsv = ref('')
    const batchLoading = ref(false)
    const batchResult = ref(null)
    
    const getStatusType = (status) => {
      const types = { 0: 'warning', 1: 'success', 2: 'danger' }
      return types[status] || 'info'
    }
    
    const getStatusText = (status) => {
      const texts = { 0: '待审核', 1: '已通过', 2: '已拒绝' }
      return texts[status] || '未知'
    }
    
    const fetchList = async () => {
      loading.value = true
      try {
        const res = await request.get('/api/admin/hand-copy', {
          params: { page: page.value, limit: pageSize.value, keyword: searchKeyword.value }
        })
        handCopyList.value = res.data || []
        total.value = res.total || 0
      } catch (error) {
        ElMessage.error('获取列表失败')
      } finally {
        loading.value = false
      }
    }
    
    // 获取分类列表
    const fetchCategories = async () => {
      try {
        const res = await request.get('/api/admin/hand-copy/categories')
        categoryOptions.value = res.data || []
      } catch (error) {
        console.error('获取分类失败', error)
      }
    }
    
    // 封面上传
    const beforeImageUpload = (file) => {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isImage) {
        ElMessage.error('只能上传图片文件')
        return false
      }
      if (!isLt2M) {
        ElMessage.error('图片大小不能超过 2MB')
        return false
      }
      return true
    }
    
    const handleCoverUpload = async (options) => {
      coverUploading.value = true
      try {
        const formData = new FormData()
        formData.append('file', options.file)
        const res = await request.post('/api/admin/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.code === 0) {
          const url = res.data.url
          addForm.value.cover_url = url
          ElMessage.success('上传成功')
        } else {
          ElMessage.error(res.message || '上传失败')
        }
      } catch (error) {
        ElMessage.error('上传失败：' + (error.message || '网络错误'))
      } finally {
        coverUploading.value = false
      }
    }
    
    // 提交添加
    const handleAddSubmit = async () => {
      if (!addFormRef.value) return
      try {
        await addFormRef.value.validate()
        addLoading.value = true
        const submitData = { ...addForm.value }
        // el-select may return array; ensure category_id is a number
        if (Array.isArray(submitData.category_id)) {
          submitData.category_id = submitData.category_id[0]
        }
        await request.post('/api/admin/hand-copy', submitData)
        ElMessage.success('添加成功')
        addDialogVisible.value = false
        addForm.value = { title: '', category_id: '', cover_url: '', content: '', difficulty: 'easy', status: 1 }
        fetchList()
      } catch (error) {
        if (error !== 'cancel' && error.message !== 'validation failed') {
          ElMessage.error('添加失败：' + (error.message || '未知错误'))
        }
      } finally {
        addLoading.value = false
      }
    }
    
    const handleSearch = () => {
      page.value = 1
      fetchList()
    }
    
    const handlePageChange = (val) => {
      page.value = val
      fetchList()
    }
    
    const handleView = (row) => {
      // 查看详情
    }
    
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确认删除该手抄报？', '提示', { type: 'warning' })
        await request.delete(`/api/admin/hand-copy/${row.id}`)
        ElMessage.success('删除成功')
        fetchList()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
    
    // 下载CSV模板
    const downloadTemplate = () => {
      const template = 'title,category_id,cover_url,content,difficulty\n手抄报标题,1,https://example.com/cover.jpg,手抄报内容描述,easy\n示例手抄报,2,https://example.com/cover2.jpg,示例内容,medium'
      const blob = new Blob(['\ufeff' + template], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '手抄报导入模板.csv'
      link.click()
      URL.revokeObjectURL(link.href)
      ElMessage.success('模板下载成功')
    }
    
    // 批量导入
    const handleBatchImport = async () => {
      batchLoading.value = true
      batchResult.value = null
      
      try {
        let res
        if (batchActiveTab.value === 'json') {
          if (!batchJson.value.trim()) {
            ElMessage.warning('请输入JSON数据')
            return
          }
          const items = JSON.parse(batchJson.value)
          res = await request.post('/api/admin/hand-copy/batch', { items })
        } else {
          if (!batchCsv.value.trim()) {
            ElMessage.warning('请输入CSV数据')
            return
          }
          res = await request.post('/api/admin/hand-copy/batch-csv', { csv: batchCsv.value })
        }
        
        batchResult.value = res
        if (res.failed === 0) {
          ElMessage.success(`成功导入 ${res.success} 条数据`)
          fetchList()
        } else {
          ElMessage.warning(`导入完成，成功 ${res.success} 条，失败 ${res.failed} 条`)
        }
      } catch (error) {
        if (error instanceof SyntaxError) {
          ElMessage.error('JSON格式错误，请检查输入')
        } else {
          ElMessage.error('导入失败：' + (error.message || '未知错误'))
        }
      } finally {
        batchLoading.value = false
      }
    }
    
    onMounted(() => {
      fetchList()
      fetchCategories()
    })
    
    return {
      handCopyList,
      loading,
      page,
      pageSize,
      total,
      searchKeyword,
      addDialogVisible,
      addForm,
      addLoading,
      coverUploading,
      categoryOptions,
      addFormRef,
      addRules,
      batchDialogVisible,
      batchActiveTab,
      batchJson,
      batchCsv,
      batchLoading,
      batchResult,
      getStatusType,
      getStatusText,
      handleSearch,
      handlePageChange,
      handleView,
      handleDelete,
      handleAddSubmit,
      beforeImageUpload,
      handleCoverUpload,
      handleBatchImport,
      downloadTemplate
    }
  }
}
</script>

<style scoped>
.handcopy-list {
  padding: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  flex-wrap: wrap;
  gap: 12px;
}

.card-header span {
  font-weight: 600;
  font-size: 16px;
  color: #303133;
}

.batch-result {
  margin-top: 20px;
}

/* 封面上传预览 */
:deep(.el-image) {
  border-radius: 8px;
  overflow: hidden;
}
</style>